import type { PQueryBuilderField } from '@pushly/aqe/lib/components/pquery-builder'

/**
 * The registry key an event-bindable field routes its value editor to.
 *
 * The builder resolves an editor as `field.inputKey ?? field.type` and consults the caller's
 * registry before its own inputs, so naming this key on a field is the whole of what routes it to
 * an editor that understands a `{ "$event": … }` value. Without one, that object reaches a text
 * control, renders as `[object Object]`, and is replaced by a string on the first keystroke.
 */
export const EVENT_BINDABLE_INPUT_KEY = 'event-bindable'

/**
 * The registered controls a bindable field keeps as its literal half.
 *
 * A field naming one of these is offered with an event property as its value *and* keeps the editor
 * it already had: the bindable editor renders the named control for the literal half rather than
 * reproducing it, so a literal is authored exactly as it is on the segment screen.
 *
 * Only controls that edit one value and nothing else belong here. A control that also writes the
 * rest of the condition — a counting window or scope into the row's meta, the property being tested,
 * a journey tree — cannot have its value replaced by one reference without leaving that remainder
 * behind, so those controls stay on the field and the field is simply not bindable.
 */
const DELEGABLE_LITERAL_INPUT_KEYS: ReadonlySet<string> = new Set([
    'geo-polygon',
    'prompt-picker',
    'notification-id-clicked',
])

/**
 * Catalog input keys that name no control of their own: the builder resolves the field to its
 * plain input for the field's type, so there is nothing to delegate to and nothing lost by binding.
 */
const PLAIN_INPUT_KEYS: ReadonlySet<string> = new Set(['app-version'])

/**
 * A catalog field, plus the control it authored literals through before it was made bindable.
 *
 * Carried on the field rather than encoded into a second registry key so that one registration
 * serves every delegating field, and so the delegation is visible on the thing that was changed.
 */
export interface EventBindableField extends PQueryBuilderField {
    /** The input this field would have resolved had it not been routed to the bindable editor. */
    literalInputKey?: string
}

/**
 * True when a field may be offered with an event property as its value.
 *
 * The aim is that a control segmentation and journeys share is the same control in both, bindable
 * or not — so this excludes only what a single reference cannot stand in for, and never by
 * swapping a field's control for a different one.
 *
 * - A nested field's value is an inner condition group, and an escape-hatch field's value input
 *   also carries the property name being tested.
 * - A locale-keyed field's property name varies with the locale chosen, so there is no one
 *   property a reference could be attached to.
 * - A field whose own control writes more than the value (see {@link DELEGABLE_LITERAL_INPUT_KEYS}).
 *
 * Whether the *operator* in play leaves room for one reference is a separate question, answered
 * per row by the editor, because the same field accepts a pair of bounds under one operator and a
 * single value under another.
 */
export function isEventBindableField(field: PQueryBuilderField): boolean {
    if (field.nested || field.allowCustomProperty) return false
    if (field.locales?.length) return false

    if (field.inputKey) {
        if (DELEGABLE_LITERAL_INPUT_KEYS.has(field.inputKey)) return true
        // Any other control is left alone and unbindable.
        return PLAIN_INPUT_KEYS.has(field.inputKey)
    }

    return true
}

/**
 * The catalog, with every event-bindable field routed to the editor that understands an event
 * reference.
 *
 * Data on the catalog rather than behaviour in the builder: which fields may hold a late-bound
 * value is decided here and read by the builder through the one key it already resolves editors by.
 *
 * Fields that are not bindable are returned by identity, so a catalog with none of them is the same
 * array it was handed — which is what keeps a query with no event references authored, serialized
 * and rendered exactly as it is today.
 */
export function withEventBindableValueInputs(fields: PQueryBuilderField[]): PQueryBuilderField[] {
    return fields.map((field) => {
        if (!isEventBindableField(field)) return field

        const bindable: EventBindableField = { ...field, inputKey: EVENT_BINDABLE_INPUT_KEY }
        if (field.inputKey) bindable.literalInputKey = field.inputKey

        return bindable
    })
}
